import { cn } from "@/utils/cn"
import { motion } from "framer-motion"
import { ArrowDown, ArrowUp, Calendar, Clock } from "lucide-react"

export type SortField = "updated" | "created"
export type SortDirection = "asc" | "desc"

export interface SortConfig {
  field: SortField
  direction: SortDirection
}

interface SortControlProps {
  /**
   * Unique ID for the component (used for framer-motion layoutId)
   */
  id?: string
  /**
   * Current sort configuration
   */
  value: SortConfig
  /**
   * Callback when sort configuration changes
   */
  onChange: (value: SortConfig) => void
  /**
   * Whether the control is disabled
   */
  disabled?: boolean
  /**
   * Size variant
   */
  size?: "sm" | "md"
  /**
   * Optional className for the container
   */
  className?: string
}

interface FieldOption {
  value: SortField
  label: string
  icon: typeof Clock
  description: string
}

const fieldOptions: FieldOption[] = [
  {
    value: "updated",
    label: "Updated",
    icon: Clock,
    description: "Sort by last updated time",
  },
  {
    value: "created",
    label: "Created",
    icon: Calendar,
    description: "Sort by creation time",
  },
]

export function SortControl({
  id,
  value,
  onChange,
  disabled = false,
  size = "sm",
  className,
}: SortControlProps) {
  const sizeClasses = {
    sm: {
      container: "p-0.5 gap-0.5",
      button: "px-2.5 py-1.5 text-xs gap-1.5",
      directionButton: "px-2 py-1.5",
      icon: "h-3 w-3",
    },
    md: {
      container: "p-1 gap-1",
      button: "px-3 py-2 text-sm gap-2",
      directionButton: "px-2.5 py-2",
      icon: "h-4 w-4",
    },
  }

  const sizes = sizeClasses[size]

  const handleFieldChange = (field: SortField) => {
    if (disabled) return
    onChange({ ...value, field })
  }

  const handleDirectionToggle = () => {
    if (disabled) return
    onChange({
      ...value,
      direction: value.direction === "asc" ? "desc" : "asc",
    })
  }

  const DirectionIcon = value.direction === "asc" ? ArrowUp : ArrowDown

  return (
    <div
      role="group"
      aria-label="Sort controls"
      data-testid={id ? `sort-control-${id}` : "sort-control"}
      className={cn(
        "relative inline-flex items-center rounded-lg bg-dark-800/80 border border-dark-700/50 backdrop-blur-sm",
        sizes.container,
        disabled && "opacity-50 cursor-not-allowed",
        className
      )}
    >
      {/* Field selector */}
      <div
        role="radiogroup"
        aria-label="Sort field"
        className="relative inline-flex"
      >
        {fieldOptions.map((option) => {
          const isSelected = option.value === value.field
          const Icon = option.icon

          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={isSelected}
              aria-label={option.label}
              title={option.description}
              disabled={disabled}
              onClick={() => handleFieldChange(option.value)}
              className={cn(
                "relative z-10 flex items-center font-medium transition-all duration-200 rounded-md",
                sizes.button,
                disabled && "cursor-not-allowed",
                !disabled && "cursor-pointer",
                isSelected
                  ? "text-white"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              {/* Animated background for selected state */}
              {isSelected && (
                <motion.div
                  layoutId={id ? `sort-field-bg-${id}` : "sort-field-bg"}
                  className="absolute inset-0 rounded-md shadow-lg bg-gradient-to-r from-dark-600 to-dark-500 shadow-dark-600/25"
                  transition={{
                    type: "spring",
                    stiffness: 400,
                    damping: 30,
                  }}
                />
              )}

              {/* Icon */}
              <motion.span
                className="relative z-10"
                initial={false}
                animate={{
                  scale: isSelected ? 1.1 : 1,
                }}
                transition={{ type: "spring", stiffness: 400, damping: 25 }}
              >
                <Icon className={sizes.icon} />
              </motion.span>

              {/* Label */}
              <span className="relative z-10">{option.label}</span>
            </button>
          )
        })}
      </div>

      {/* Divider */}
      <div className="w-px h-5 bg-dark-600/50 mx-0.5" />

      {/* Direction toggle button */}
      <button
        type="button"
        aria-label={value.direction === "asc" ? "Sort ascending (oldest first)" : "Sort descending (newest first)"}
        title={value.direction === "asc" ? "Oldest first — click to reverse" : "Newest first — click to reverse"}
        disabled={disabled}
        onClick={handleDirectionToggle}
        className={cn(
          "relative z-10 flex items-center justify-center font-medium transition-all duration-200 rounded-md",
          sizes.directionButton,
          disabled && "cursor-not-allowed",
          !disabled && "cursor-pointer",
          "text-muted-foreground hover:text-foreground hover:bg-dark-700/50"
        )}
      >
        <motion.span
          key={value.direction}
          initial={{ rotate: value.direction === "asc" ? 180 : 0, opacity: 0 }}
          animate={{ rotate: 0, opacity: 1 }}
          exit={{ rotate: value.direction === "asc" ? -180 : 180, opacity: 0 }}
          transition={{ type: "spring", stiffness: 400, damping: 25 }}
        >
          <DirectionIcon className={sizes.icon} />
        </motion.span>
      </button>
    </div>
  )
}
